import React, { useState } from 'react';
import styles from './correction.module.css';
import p4 from '../../assets/img/Upper.png';
import p5 from '../../assets/img/Lower.png';
import p6 from '../../assets/img/right side.png';
import p7 from '../../assets/img/front teeth.png';
import p8 from '../../assets/img/left side.png';
import r3 from "../../assets/img/upper scan.png";
import r4 from "../../assets/img/lower scan.png";
import { doc, updateDoc, serverTimestamp, arrayUnion } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../../firebase/firebase';

const Correction = ({ patient, onBack }) => {
    const [images, setImages] = useState({
        image4: patient?.images?.img4 || null,
        image6: patient?.images?.img6 || null,
        image7: patient?.images?.img7|| null,
        image8: patient?.images?.img8 || null,
        image9: patient?.images?.img9 || null,
        image12: patient?.images?.img12 || null,
        image13: patient?.images?.img13 || null
    });
    
    const [uploading, setUploading] = useState({
        image4: false,
        image6: false,
        image7: false,
        image8: false,
        image9: false,
        image12: false,
        image13: false
    });
    
    const [uploadedFiles, setUploadedFiles] = useState({
        image4: null,
        image6: null,
        image7: null,
        image8: null,
        image9: null,
        image12: null,
        image13: null
    });
    
    const [saving, setSaving] = useState(false);

    const handleUpload = async (e, imageKey) => {
        const file = e.target.files[0];
        if (file) {
            // Check file size for images (15MB limit)
            const isImageFile = !['image12', 'image13'].includes(imageKey);
            if (isImageFile && file.size > 15 * 1024 * 1024) {
                alert("La taille maximale de l'image est de 15 Mo");
                return;
            }
            
            // Check file type for scans
            if (['image12', 'image13'].includes(imageKey)) {
                if (!file.name.endsWith('.stl') && !file.name.endsWith('.ply')) {
                    alert("Veuillez sélectionner un fichier STL ou PLY valide");
                    return;
                }
            }
            
            setUploading(prev => ({
                ...prev,
                [imageKey]: true
            }));
            
            try {
                // Create storage reference
                const fileExtension = file.name.split('.').pop().toLowerCase();
                const storageRef = ref(storage, `corrections/${patient.id}/${imageKey}_${Date.now()}.${fileExtension}`);
                
                // Upload file
                const snapshot = await uploadBytes(storageRef, file);
                const downloadURL = await getDownloadURL(snapshot.ref);
                
                // Update state
                setImages(prev => ({
                    ...prev,
                    [imageKey]: downloadURL
                }));
                
                setUploadedFiles(prev => ({
                    ...prev,
                    [imageKey]: {
                        url: downloadURL,
                        fileName: file.name,
                        fileSize: file.size,
                        uploadDate: new Date().toISOString()
                    }
                }));
                
            } catch (error) {
                console.error(`Error uploading ${imageKey}:`, error);
                alert(`Erreur lors de l'upload du fichier: ${error.message}`);
            } finally {
                setUploading(prev => ({
                    ...prev,
                    [imageKey]: false
                }));
            }
        }
    };

    const handleSubmit = async () => {
        try {
            setSaving(true);
            
            // Validate that we have at least some images or files
            const hasAnyImages = Object.values(images).some(img => img !== null) || 
                                Object.values(uploadedFiles).some(file => file !== null);
            
            if (!hasAnyImages) {
                alert("Veuillez uploader au moins une image ou fichier avant de soumettre la demande");
                return;
            }
            
            // Create correction object with only file links and timestamps
            const correctionData = {
                requestDate: new Date().toISOString(),
                images: {
                    img4: uploadedFiles.image4?.url || images.image4 || null,
                    img6: uploadedFiles.image6?.url || images.image6 || null,
                    img7: uploadedFiles.image7?.url || images.image7 || null,
                    img8: uploadedFiles.image8?.url || images.image8 || null,
                    img9: uploadedFiles.image9?.url || images.image9 || null,
                    img12: uploadedFiles.image12?.url || images.image12 || null,
                    img13: uploadedFiles.image13?.url || images.image13 || null
                },
                requestTimestamp: new Date().toISOString()
            };

            console.log('Submitting correction data:', correctionData);

            // Get the current patient document to check if corrections array exists
            const patientRef = doc(db, "patients", patient.id);
            
            // Add correction to the corrections array in Firestore
            await updateDoc(patientRef, {
                corrections: arrayUnion(correctionData),
                lastModified: serverTimestamp()
            });
            
            alert("Demande de correction soumise avec succès");
            onBack();
        } catch (error) {
            console.error("Error saving correction request:", error);
            if (error.code === 'permission-denied') {
                alert("Erreur de permissions. Veuillez vérifier vos droits d'accès.");
            } else if (error.code === 'not-found') {
                alert("Patient non trouvé dans la base de données.");
            } else {
                alert(`Erreur lors de la soumission de la demande de correction: ${error.message}`);
            }
        } finally {
            setSaving(false);
        }
    };

    const getImagePlaceholder = (imageName) => {
        switch(imageName) {
            case 'image4': return p4;
            case 'image6': return p5;
            case 'image7': return p6;
            case 'image8': return p7;
            case 'image9': return p8;
            case 'image12': return r3;
            case 'image13': return r4;
            default: return p4;
        }
    };

    const isAnyUploading = Object.values(uploading).some(uploading => uploading);

    return (
        <div className={styles.correctionSection}>
            <div className={styles.header}>
                <button className={styles.backButton} onClick={onBack}>
                    ← Retour
                </button>
                <h2 className={styles.correctionTitle}>Demande de correction</h2>
            </div>

            <div className={styles.photoSection}>
                <h3>Photos intraorales <span className={styles.required}>*</span></h3>
                <p className={styles.uploadInstruction}>
                    Selectionner (depuis votre ordinateur) / utiliser l'application Diamond Clinic
                    <span className={styles.fileSizeLimit}> - Taille max de l'image: 15Mo</span>
                </p>

                <div className={styles.photoGrid}>
                    <div className={styles.photoUploadBox}>
                        <div className={styles.imageContainer}>
                            <img 
                                src={images.image4 || patient?.images?.img4 || p4} 
                                alt="Upper Arch"
                                className={styles.galleryImage}
                                onError={(e) => {
                                    e.target.src = p4;
                                }}
                            />
                            {uploading.image4 && (
                                <div className={styles.uploadOverlay}>
                                    <div className={styles.loadingSpinner}></div>
                                </div>
                            )}
                        </div>
                        <label className={styles.uploadBtn} htmlFor="image4-input" disabled={uploading.image4}>
                            {uploadedFiles.image4 ? 'Changer la photo +' : 'Select photo to upload +'}
                        </label>
                        <input
                            id="image4-input"
                            type="file"
                            accept="image/*"
                            onChange={(e) => handleUpload(e, 'image4')}
                            className={styles.hiddenInput}
                            disabled={uploading.image4}
                        />
                        <span className={styles.photoLabel}>Occlusale supérieure <span className={styles.required}>*</span></span>
                    </div>

                    <div className={styles.photoUploadBox}>
                        <div className={styles.imageContainer}>
                            <img 
                                src={images.image6 || patient?.images?.img6 || p5} 
                                alt="Lower Arch"
                                className={styles.galleryImage}
                                onError={(e) => {
                                    e.target.src = p5;
                                }}
                            />
                            {uploading.image6 && (
                                <div className={styles.uploadOverlay}>
                                    <div className={styles.loadingSpinner}></div>
                                </div>
                            )}
                        </div>
                        <label className={styles.uploadBtn} htmlFor="image6-input" disabled={uploading.image6}>
                            {uploadedFiles.image6 ? 'Changer la photo +' : 'Select photo to upload +'}
                        </label>
                        <input
                            id="image6-input"
                            type="file"
                            accept="image/*"
                            onChange={(e) => handleUpload(e, 'image6')}
                            className={styles.hiddenInput}
                            disabled={uploading.image6}
                        />
                        <span className={styles.photoLabel}>Occlusale inférieure <span className={styles.required}>*</span></span>
                    </div>

                    <div className={styles.photoUploadBox}>
                        <div className={styles.imageContainer}>
                            <img 
                                src={images.image7 || patient?.images?.img7 || p6} 
                                alt="Right Side"
                                className={styles.galleryImage}
                                onError={(e) => {
                                    e.target.src = p6;
                                }}
                            />
                            {uploading.image7 && (
                                <div className={styles.uploadOverlay}>
                                    <div className={styles.loadingSpinner}></div>
                                </div>
                            )}
                        </div>
                        <label className={styles.uploadBtn} htmlFor="image7-input" disabled={uploading.image7}>
                            {uploadedFiles.image7 ? 'Changer la photo +' : 'Select photo to upload +'}
                        </label>
                        <input
                            id="image7-input"
                            type="file"
                            accept="image/*"
                            onChange={(e) => handleUpload(e, 'image7')}
                            className={styles.hiddenInput}
                            disabled={uploading.image7}
                        />
                        <span className={styles.photoLabel}>Intra-orale latérale droite <span className={styles.required}>*</span></span>
                    </div>
                </div>

                <div className={styles.photoGrid}>
                    <div className={styles.photoUploadBox}>
                        <div className={styles.imageContainer}>
                            <img 
                                src={images.image8 || patient?.images?.img8 || p7} 
                                alt="Front View"
                                className={styles.galleryImage}
                                onError={(e) => {
                                    e.target.src = p7;
                                }}
                            />
                            {uploading.image8 && (
                                <div className={styles.uploadOverlay}>
                                    <div className={styles.loadingSpinner}></div>
                                </div>
                            )}
                        </div>
                        <label className={styles.uploadBtn} htmlFor="image8-input" disabled={uploading.image8}>
                            {uploadedFiles.image8 ? 'Changer la photo +' : 'Select photo to upload +'}
                        </label>
                        <input
                            id="image8-input"
                            type="file"
                            accept="image/*"
                            onChange={(e) => handleUpload(e, 'image8')}
                            className={styles.hiddenInput}
                            disabled={uploading.image8}
                        />
                        <span className={styles.photoLabel}>Intra-orale de face <span className={styles.required}>*</span></span>
                    </div>

                    <div className={styles.photoUploadBox}>
                        <div className={styles.imageContainer}>
                            <img 
                                src={images.image9 || patient?.images?.img9 || p8} 
                                alt="Left Side"
                                className={styles.galleryImage}
                                onError={(e) => {
                                    e.target.src = p8;
                                }}
                            />
                            {uploading.image9 && (
                                <div className={styles.uploadOverlay}>
                                    <div className={styles.loadingSpinner}></div>
                                </div>
                            )}
                        </div>
                        <label className={styles.uploadBtn} htmlFor="image9-input" disabled={uploading.image9}>
                            {uploadedFiles.image9 ? 'Changer la photo +' : 'Select photo to upload +'}
                        </label>
                        <input
                            id="image9-input"
                            type="file"
                            accept="image/*"
                            onChange={(e) => handleUpload(e, 'image9')}
                            className={styles.hiddenInput}
                            disabled={uploading.image9}
                        />
                        <span className={styles.photoLabel}>Intra-orale latérale gauche <span className={styles.required}>*</span></span>
                    </div>
                </div>
            </div>

            <div className={styles.scanSection}>
                <h3>Scans</h3>
                <div className={styles.photoGrid}>
                    <div className={styles.photoUploadBox}>
                        <div className={styles.imageContainer}>
                            <img 
                                src={r3} 
                                alt="upper scan" 
                                className={styles.galleryImage}
                            />
                            {uploading.image12 && (
                                <div className={styles.uploadOverlay}>
                                    <div className={styles.loadingSpinner}></div>
                                </div>
                            )}
                            {uploadedFiles.image12 && !uploading.image12 && (
                                <div className={styles.uploadOverlay}>
                                    <div className={styles.checkIconOverlay}>
                                        <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3">
                                            <polyline points="20 6 9 17 4 12"></polyline>
                                        </svg>
                                    </div>
                                </div>
                            )}
                        </div>
                        <label className={styles.uploadBtn} htmlFor="image12-input" disabled={uploading.image12}>
                            {uploadedFiles.image12 ? 'Changer le fichier +' : 'Select file to upload +'}
                        </label>
                        <input
                            id="image12-input"
                            type="file"
                            accept=".stl,.ply"
                            onChange={(e) => handleUpload(e, 'image12')}
                            className={styles.hiddenInput}
                            disabled={uploading.image12}
                        />
                        <span className={styles.photoLabel}>Maxillaire <span className={styles.required}>*</span></span>
                    </div>

                    <div className={styles.photoUploadBox}>
                        <div className={styles.imageContainer}>
                            <img 
                                src={r4} 
                                alt="lower scan" 
                                className={styles.galleryImage}
                            />
                            {uploading.image13 && (
                                <div className={styles.uploadOverlay}>
                                    <div className={styles.loadingSpinner}></div>
                                </div>
                            )}
                            {uploadedFiles.image13 && !uploading.image13 && (
                                <div className={styles.uploadOverlay}>
                                    <div className={styles.checkIconOverlay}>
                                        <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3">
                                            <polyline points="20 6 9 17 4 12"></polyline>
                                        </svg>
                                    </div>
                                </div>
                            )}
                        </div>
                        <label className={styles.uploadBtn} htmlFor="image13-input" disabled={uploading.image13}>
                            {uploadedFiles.image13 ? 'Changer le fichier +' : 'Select file to upload +'}
                        </label>
                        <input
                            id="image13-input"
                            type="file"
                            accept=".stl,.ply"
                            onChange={(e) => handleUpload(e, 'image13')}
                            className={styles.hiddenInput}
                            disabled={uploading.image13}
                        />
                        <span className={styles.photoLabel}>Mandibule <span className={styles.required}>*</span></span>
                    </div>
                </div>
            </div>

            <div className={styles.submitSection}>
                <button 
                    className={styles.submitButton}
                    onClick={handleSubmit}
                    disabled={saving || isAnyUploading}
                >
                    {saving ? 'Soumission en cours...' : 
                     isAnyUploading ? 'Veuillez attendre la fin des uploads...' : 
                     'Soumettre la demande'}
                </button>
            </div>
        </div>
    );
};

export default Correction; 